import { useState } from 'react'
import { FiX, FiClock, FiCalendar, FiAlertCircle, FiSend } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { Btn } from '../../ui'
import { formatearFecha, formatearHora } from '../../utils/fechas'

/*
 * PropuestaCorreccionModal (V2) — DS v2.
 * Admin propone una corrección al empleado, que decide si aceptarla.
 */

const PropuestaCorreccionModalV2 = ({ fichaje, onClose, onSuccess, buildApiUrl }) => {
  const [motivo, setMotivo] = useState('')
  const [nuevaHoraEntrada, setNuevaHoraEntrada] = useState(fichaje?.hora_entrada?.substring(0, 5) || '')
  const [nuevaHoraSalida, setNuevaHoraSalida] = useState(fichaje?.hora_salida?.substring(0, 5) || '')
  const [loading, setLoading] = useState(false)

  const apiUrl = buildApiUrl ? buildApiUrl('/correcciones/proponer') : `${API_URL}/correcciones/proponer`

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!motivo.trim()) { toast.error('Debes indicar el motivo de la propuesta de corrección'); return }
    if (!nuevaHoraEntrada) { toast.error('Debes indicar la hora de entrada corregida'); return }

    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          fichaje_id: fichaje.id,
          empleado_id: fichaje.empleado_id,
          hora_entrada_corregida: nuevaHoraEntrada,
          hora_salida_corregida: nuevaHoraSalida || null,
          motivo
        })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Error al enviar propuesta')
      toast.success('Propuesta de corrección enviada al empleado')
      onSuccess()
    } catch (error) {
      console.error('Error:', error)
      toast.error(error.message || 'Error al enviar propuesta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="eyebrow mb-1">Propuesta admin</div>
            <h3 className="font-display text-[22px] text-[var(--ink)] leading-tight">Proponer corrección.</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Info fichaje */}
        <div className="p-4 bg-[var(--bg-2)] border-b border-[var(--line)]">
          <div className="flex items-center gap-2 text-[12.5px] text-[var(--ink-3)] mb-2 flex-wrap">
            <FiCalendar size={13} />
            <span className="font-medium text-[var(--ink-2)]">{fichaje.empleado_nombre || 'Empleado'}</span>
            <span>·</span>
            <span className="capitalize">
              {formatearFecha(fichaje.fecha)}
            </span>
          </div>
          <div className="flex items-center gap-4 text-[13px]">
            <div className="flex items-center gap-1.5">
              <FiClock size={13} className="text-[#0F8C66]" />
              <span className="text-[var(--ink-3)]">Entrada:</span>
              <strong className="font-mono tnum text-[var(--ink)]">{formatearHora(fichaje.hora_entrada)}</strong>
            </div>
            {fichaje.hora_salida && (
              <div className="flex items-center gap-1.5">
                <FiClock size={13} className="text-[#C13030]" />
                <span className="text-[var(--ink-3)]">Salida:</span>
                <strong className="font-mono tnum text-[var(--ink)]">{formatearHora(fichaje.hora_salida)}</strong>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="eyebrow block mb-1.5">Motivo de la propuesta *</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] resize-none focus:outline-none focus:border-[var(--ink-3)]"
              placeholder="Explica al empleado por qué propones esta corrección…"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1.5">Hora entrada corregida *</label>
              <input
                type="time"
                value={nuevaHoraEntrada}
                onChange={(e) => setNuevaHoraEntrada(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                required
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Hora salida corregida</label>
              <input
                type="time"
                value={nuevaHoraSalida}
                onChange={(e) => setNuevaHoraSalida(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.20)]">
            <FiAlertCircle className="text-[#A86A06] mt-0.5 flex-shrink-0" size={14} />
            <p className="text-[11.5px] text-[#A86A06]">
              La propuesta será enviada al empleado para su aprobación. El fichaje solo se modificará cuando el empleado la acepte.
            </p>
          </div>

          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={loading}>
              Cancelar
            </Btn>
            <Btn type="submit" variant="primary" className="flex-1" disabled={loading}>
              <FiSend size={13} />
              {loading ? 'Enviando…' : 'Enviar propuesta'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PropuestaCorreccionModalV2
